import { yup } from '@framework/validation/initYup.ts';
import { FormBuilderMetaFlags } from '../../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { getWidgetName } from '../../../../../validator/form-builder/getWidgetName.ts';
import { setOptionEnabledCondition } from '../../../../../validator/setEnabledCondition.ts';
import { setOptionVisibleCondition } from '../../../../../validator/setVisibleCondition.ts';
import { type ICreateWidgetFormSchemaInput } from '../../../IWidgetType.ts';
import { type IWidgetAdvancedOption } from '../../../Widget.ts';
import {
  createAdvancedSelectValueSchema,
  type IWidgetAdvancedOptionValue,
} from '../advanced-select-common/form/createAdvancedSelectValueSchema.ts';
import { getUniqueOptionValue } from '../advanced-select-common/utils/geUniqueOptionValue.ts';
import { createMultiSelectObjectSchema } from '../multi-select/createMultiSelectObjectSchema.ts';
import { multiSelectOptionsObjectName } from '../multi-select/getMultiSelectOptionsObjectName.ts';
import { type IAdvancedMultiSelectWidget } from './IAdvancedMultiSelectWidget.ts';

/**
 * Trasforma l'oggetto _options@internal in array di oggetti completi.
 */
function transformOptionsToArray(
  optionsObjectValue: Record<string, boolean>,
  optionIdsMap: Map<string, IWidgetAdvancedOption>,
): IWidgetAdvancedOptionValue[] {
  const selectedOptionIds = Object.entries(optionsObjectValue || {})
    .filter(([key, value]) => value)
    .map(([key]) => key);

  return selectedOptionIds
    .map(optionId => {
      const option = optionIdsMap.get(optionId);
      if (!option) return null;
      return { ...option.values };
    })
    .filter(Boolean) as IWidgetAdvancedOptionValue[];
}

/**
 * Crea lo schema di validazione per il widget di selezione multipla avanzata
 */
export function createAdvancedMultiSelectFormSchema(
  input: ICreateWidgetFormSchemaInput<IAdvancedMultiSelectWidget>,
) {
  const { widget } = input;
  const name = getWidgetName(widget);
  const columns = widget.columns ?? [];

  const optionsObjectName = multiSelectOptionsObjectName(name);

  const optionsMap = new Map(
    widget.options.map(option => [
      getUniqueOptionValue(widget, option),
      option,
    ]),
  );

  // Valori ammessi: usa la chiave univoca per ogni opzione
  const possibleOptions = Array.from(optionsMap.keys());

  // Schema che salva array di oggetti completi nel campo principale
  // La transform converte da _options@internal agli oggetti con tutte le colonne
  // TODO Centralizzare con `multiSelect`
  const listSchema = yup
    .array()
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipEnabledCondition,
    )
    .meta({
      [FormBuilderMetaFlags.SerializeSiblingOptions]: possibleOptions,
      /**
       * A differenza del multi-select normale, qui le chiavi delle opzioni
       * sono derivate da una colonna specifica, non dal valore dell'opzione
       * (che è un oggetto con più colonne).
       */
      [FormBuilderMetaFlags.SerializeSiblingOptionsKeyField]:
        widget.uniqueValueColumnId,
    })
    .of(createAdvancedSelectValueSchema(widget))
    .default(() => [])
    .when([optionsObjectName], ([optionsObjectValue], schema) => {
      return schema.transform(() => {
        return transformOptionsToArray(optionsObjectValue, optionsMap);
      });
    });

  const optionsObjectSchema = createMultiSelectObjectSchema({
    widget,
    optionsObjectName,
    optionsWithKeys: widget.options.map(o => ({
      option: o,
      key: getUniqueOptionValue(widget, o),
    })),
    createOptionSchema,
  });

  return {
    [name]: listSchema.notRequired(),
    [optionsObjectName]: optionsObjectSchema.notRequired(),
  };
}

function createOptionSchema(
  parentName: string,
  widget: IAdvancedMultiSelectWidget,
  option: IWidgetAdvancedOption,
) {
  let schema = yup.boolean().notRequired();
  schema = setOptionEnabledCondition(schema, widget, option);
  schema = setOptionVisibleCondition(schema, widget, option);
  return schema;
}
